import React, {useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {getServiceAccounts} from "redux/actions/servicesActions";
import {validateText} from "redux/actions/settingsActions";
import {postTemplatesPreview} from "redux/api/messagesAPI";
import {searchCommand, stripHtmlTags} from "utilities/helperFunctions";
import {useFeatureFlags} from "utilities/featureUtil";
import _ from "lodash";

import {useTheme} from "connectui/theme/ThemeProvider";
import {buildStyles} from "connectui/component/buildStyles";
import EmailInput from "connectui/component/input/text/type/email/EmailInput";
import TextInput from "connectui/component/input/text/type/TextInput";
import Container from "connectui/component/layout/Container";
import WhatsappTemplateSelector from "ui/component/inbox/conversations/input/WhatsappTemplateSelector";
import NoteInput from "./NoteInput";
import TemplatesMenu from "./TemplatesMenu";

const useStyles = (theme) =>
  buildStyles({
    input: {
      flex: 1,
      width: "100%",
      height: "100%",
      padding: theme.spacing(0, 4),
      "&.disabled": {
        backgroundColor: theme.colors.white,
        cursor: "not-allowed",
      },
    },
  });

const ConversationInputComponent = ({
  guestId,
  channel,
  inputText,
  setInputText,
  setNoteText,
  componentProps = {},
  bpLink,
  templatesData = {},
  setTemplatesData,
}) => {
  const {theme, isMobile} = useTheme();
  const classes = useStyles(theme);
  const {cannedResponses} = useFeatureFlags();
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const menuRef = useRef(null);
  const inputRef = useRef(null);
  const emailInputRef = useRef(null);
  const initialEmailUpdate = useRef(true);
  const emailContentChanged = useRef(false);
  const [searchTemplate, setSearchTemplate] = useState("");
  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState(-1);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [emailCommandText, setEmailCommandText] = useState(null);
  const [disableTemplatesMenu, setDisableTemplatesMenu] = useState(false);

  const serviceAccounts = useSelector(
    (state) => state.defaultReducer.service_accounts,
  );
  const user_profile = useSelector(
    (state) => state.defaultReducer.user_profile,
  );

  const quillEditor = emailInputRef.current?.getEditor();

  const {
    open: templatesOpen,
    loading: loadingTemplates,
    templates = [],
    buttonClicked,
  } = templatesData;
  const {
    fullEmailInput,
    emailContent,
    setEmailContent,
    whatsappData,
    setWhatsappData,
  } = componentProps;
  const showTemplates =
    templatesOpen && cannedResponses && !disableTemplatesMenu;
  const inputProps = isMobile
    ? {
        style: {
          width: "100%",
          minHeight: "24px",
          maxHeight: "120px",
          resize: "none",
          color: theme.colors.textPrimary,
          backgroundColor: theme.colors.white,
          cursor: "text",
          ...theme.typography.textXS.regular,
          fontSize: "13px",
        },
        onInput: (e) => {
          e.target.style.height = "auto";
          e.target.style.height = e.target.scrollHeight + "px";
        },
      }
    : {
        style: {
          width: "100%",
          height: "100%",
          resize: "none",
          color: theme.colors.textPrimary,
          backgroundColor: theme.colors.white,
          cursor: "text",
          ...theme.typography.textXS.regular,
        },
      };

  useEffect(() => {
    if (buttonClicked) {
      setDisableTemplatesMenu(false);
      if (!filteredTemplates.length) {
        setFilteredTemplates(templates);
        setSelectedTemplateIndex(-1);
      }
      if (channel === "email") {
        if (quillEditor) {
          const endIndex = quillEditor.getLength();
          emailInputRef.current.focus();
          let newContent = stripHtmlTags(quillEditor.root.innerHTML)?.trim();
          if (!newContent.endsWith("/")) {
            quillEditor.insertText(endIndex - 1, "/");
            quillEditor.setSelection(endIndex, 0);
          } else {
            quillEditor.setSelection(endIndex, 0);
          }
        }
      } else {
        if (inputRef.current) {
          inputRef.current.focus();
        }
        if (!inputText.endsWith("/")) {
          setInputText((prev) => prev + "/");
        }
      }
      setTemplatesData({...templatesData, buttonClicked: false});
    }
  }, [buttonClicked]);

  useEffect(() => {
    if (
      !Object.keys(serviceAccounts?.accounts ?? {}).length ||
      !serviceAccounts?.accounts?.whatsapp ||
      !serviceAccounts?.accounts?.twilio
    ) {
      dispatch(
        getServiceAccounts({
          onSuccess: (r) => {
            handleWhatsappServiceAccount(r.accounts);
          },
        }),
      );
    } else {
      handleWhatsappServiceAccount(serviceAccounts?.accounts);
    }
  }, []);

  useEffect(() => {
    // Reset data when switching conversations
    initialEmailUpdate.current = true;
    emailContentChanged.current = false;
    if (whatsappData.accountId) {
      getTemplatesPreview(whatsappData.accountId);
    }
  }, [guestId]);

  useEffect(() => {
    if (!emailContentChanged.current) {
      setEmailContent((prev) => ({
        ...prev,
        html: `${inputText}${!!user_profile.email_signature ? `<p><br></p>${user_profile.email_signature}` : ""}`,
      }));
    }
  }, [inputText, setEmailContent]);

  const handleWhatsappServiceAccount = (servAccnts) => {
    let account = servAccnts.whatsapp?.[0] || servAccnts.twilio?.[0];
    let accountEnabled =
      !!servAccnts.whatsapp?.[0] ||
      servAccnts.twilio?.[0]?.properties.whatsapp_status == "ONLINE";
    if (!!accountEnabled) {
      setWhatsappData((prev) => ({
        ...prev,
        accountId: account.service_id,
        templates:
          account.properties?.templates?.filter(
            (t) => !!servAccnts.whatsapp?.[0] || t.status === "approved",
          ) ?? [],
      }));
      getTemplatesPreview(account.service_id);
    }
  };

  const getTemplatesPreview = (accountId) => {
    if (["chat", "profile"].includes(guestId)) return;
    let templatesPreviewBody = {
      template_type: "whatsapp",
      enso_key: user_profile.enso_key,
      guest_id: guestId,
      service_id: accountId,
    };
    postTemplatesPreview({body: templatesPreviewBody}).then((result) => {
      setWhatsappData((prev) => ({...prev, preview: result}));
    });
  };

  const getInputHeight = () => {
    if (channel === "email") {
      if (fullEmailInput) return "430px";
      else return "100px";
    }
    if (channel === "whatsapp" && whatsappData.selected) {
      return "250px";
    }
    if (channel === "note") {
      return "215px";
    }
    return "140px";
  };

  const handleSelectResponse = (response, enterKey) => {
    closeTemplates();
    setDisableTemplatesMenu(true);
    dispatch(
      validateText({
        guest_id: guestId,
        replace_text: true,
        text: response.text,
        format: "text",
        onSuccess: (filteredResponse) => {
          setSelectResponse(response, filteredResponse.replaced_text, enterKey);
        },
      }),
    );
  };

  // Run after 'validateText' action dispatch
  const setSelectResponse = (response, replaced_text, enterKey) => {
    let prevText = "";
    let nextText = "";
    let lastIndexOfSlash = -1;
    let endOfCommandIndex = -1;
    if (channel !== "email") {
      lastIndexOfSlash = inputText.lastIndexOf("/");
      endOfCommandIndex = lastIndexOfSlash + searchTemplate.length + 1;
      prevText = inputText.substring(0, lastIndexOfSlash);
      nextText = inputText.substring(lastIndexOfSlash + endOfCommandIndex);
    }
    if (response.text === "insert_BP" && channel !== "email") {
      setInputText((prev) => prevText + bpLink + nextText);
    } else {
      if (channel === "email") {
        setEmailCommandText({
          search: "/" + searchTemplate + (enterKey ? "\n" : ""),
          result: replaced_text,
        });
      } else {
        setInputText((prev) => prevText + replaced_text + nextText);
      }
    }
    setFilteredTemplates([]);
  };

  const openTemplates = () => {
    setTemplatesData && setTemplatesData({...templatesData, open: true});
  };
  const closeTemplates = () => {
    setTemplatesData && setTemplatesData({...templatesData, open: false});
  };

  const scrollToItem = (index) => {
    const menuItem = menuRef.current?.querySelectorAll(":scope > div")[index];
    if (menuItem) {
      menuItem.scrollIntoView({block: "nearest", behavior: "smooth"});
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "/") {
      setDisableTemplatesMenu(false);
    }
    if (!!templatesOpen && !!filteredTemplates.length) {
      if (filteredTemplates.length > 0) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setSelectedTemplateIndex((prevIndex) => {
            const newIndex =
              prevIndex < filteredTemplates.length - 1 ? prevIndex + 1 : 0;
            scrollToItem(newIndex);
            return newIndex;
          });
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setSelectedTemplateIndex((prevIndex) => {
            const newIndex =
              prevIndex > 0
                ? prevIndex > filteredTemplates.length - 1
                  ? filteredTemplates.length - 1
                  : prevIndex - 1
                : filteredTemplates.length - 1;
            scrollToItem(newIndex);
            return newIndex;
          });
        } else if (e.key === "Enter" && selectedTemplateIndex >= 0) {
          e.preventDefault();
          handleSelectResponse(filteredTemplates[selectedTemplateIndex], true);
        } else if (e.key === "Escape") {
          setDisableTemplatesMenu(true);
          setFilteredTemplates([]);
          closeTemplates();
        }
      }
    }
  };

  const handleEmailChange = (newContent) => {
    if (initialEmailUpdate.current) {
      initialEmailUpdate.current = false;
      const prev = JSON.stringify(emailContent);
      const next = JSON.stringify(newContent);
      if (prev === next) return;
    }
    emailContentChanged.current = true;
    setEmailContent((prev) => ({...prev, ...newContent}));
    if (cannedResponses) {
      const emailText = stripHtmlTags(newContent?.html ?? "")?.trim();
      if (emailText.includes("/")) {
        const {results, searchText} = searchCommand(emailText, templates);
        setSearchTemplate((prev) => searchText);
        setFilteredTemplates(results);
        openTemplates();
      } else {
        setSearchTemplate((prev) => "");
        setFilteredTemplates([]);
        closeTemplates();
      }
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputText(val);
    if (cannedResponses) {
      if (val.includes("/")) {
        const {results, searchText} = searchCommand(val, templates);
        setSearchTemplate((prev) => searchText);
        setFilteredTemplates(results);
        if (results.length > 0) {
          openTemplates();
        } else {
          closeTemplates();
        }
      } else {
        setSearchTemplate((prev) => "");
        setFilteredTemplates([]);
        closeTemplates();
      }
    }
  };

  const getInput = () => {
    const emailAnchorEl = emailInputRef.current?.getEditorRoot();
    const inputAnchorEl = inputRef.current;

    if (channel === "whatsapp" && !whatsappData.freeText)
      return (
        <WhatsappTemplateSelector
          data={whatsappData}
          setData={setWhatsappData}
          inputText={inputText}
          onChange={setInputText}
        />
      );

    if (channel === "note")
      return <NoteInput guestId={guestId} setNoteText={setNoteText} />;

    return (
      <>
        <TemplatesMenu
          open={showTemplates}
          menuRef={menuRef}
          skeleton={{enabled: loadingTemplates}}
          searchText={searchTemplate}
          offset={emailAnchorEl ? 16 : 0}
          data={searchTemplate ? filteredTemplates : templates}
          selectedIndex={selectedTemplateIndex}
          anchorEl={emailAnchorEl ?? inputAnchorEl}
          onClose={closeTemplates}
          onSelect={handleSelectResponse}
        />
        {channel === "email" ? (
          <EmailInput
            ref={emailInputRef}
            commandText={emailCommandText}
            clearCommandText={() => setEmailCommandText(null)}
            imagePath={`${user_profile.enso_key}/messages/${guestId}/email_images`}
            email={emailContent}
            onKeyDown={handleKeyDown}
            onChange={handleEmailChange}
          />
        ) : (
          <TextInput
            as={"textarea"}
            ref={inputRef}
            className={classes.input}
            inputProps={{
              value: inputText,
              onChange: handleInputChange,
              onKeyDown: handleKeyDown,
              placeholder:
                cannedResponses && !isMobile
                  ? t("inbox.conversations.input.placeholder.templates")
                  : t("inbox.conversations.input.placeholder.no-templates"),
              ...inputProps,
            }}
          />
        )}
      </>
    );
  };

  if (isMobile) return getInput();
  return (
    <Container
      sx={{
        width: "100%",
        height: getInputHeight(),
      }}
    >
      {getInput()}
    </Container>
  );
};

export default ConversationInputComponent;
